import type { ModuleDefinition, ModuleSection } from './types';

/**
 * The navigation model (docs/architecture/navigation-and-breadcrumbs.md §3).
 *
 * The drawer, the breadcrumb trail and routing all derive from THIS — and from the
 * URL, never from component state alone. That is what keeps deep links and the
 * browser back button working: any route can be rendered cold.
 */

/** The root route, owned by the shell rather than by a module. */
export const HOME_PATH = '/';

export interface NavigationLocation {
  /** True at the root launcher — the drawer shows all applications. */
  readonly isRoot: boolean;
  readonly module?: ModuleDefinition;
  /**
   * Every section traversed to reach this URL, outermost first.
   *
   * The model nests to ANY depth, because the products do. APR reaches four levels —
   * `/apr` → Application Evaluation → APR Model & Execution → APR Model — while
   * Administration reaches two. A fixed set of named levels would have to grow again
   * the first time a product nests one deeper, so the trail is a list.
   */
  readonly trail: readonly ModuleSection[];
  /** The screen itself — the last of `trail`, and only when it has no children. */
  readonly section?: ModuleSection;
  /** Trailing URL segment below a screen, e.g. a record id. */
  readonly recordId?: string;
}

export interface Breadcrumb {
  readonly label: string;
  /** Absent on the current page — the last crumb is text, not a link. */
  readonly path?: string;
}

/**
 * Resolves a URL to a place in the navigation model.
 *
 * `modules` should already be filtered to what this user may see, so a route to an
 * unlicensed or unassigned module resolves to nothing and the caller can redirect.
 */
export function resolveNavigation(
  pathname: string,
  modules: readonly ModuleDefinition[],
): NavigationLocation {
  const [moduleSegment, ...rest] = pathname.split('/').filter(Boolean);

  if (!moduleSegment) return { isRoot: true, trail: [] };

  const module = modules.find((m) => m.id === moduleSegment);
  if (!module) return { isRoot: true, trail: [] };

  // Walk the section tree one URL segment at a time. A segment that names a child of
  // the current level descends; the first that does not is where the sections end.
  //
  // How deep a URL goes is therefore a property of the CATALOGUE, not of the URL —
  // which is what lets a two-level module and a four-level one share this resolver,
  // and what stops a record id being mistaken for a section (or the reverse).
  const trail: ModuleSection[] = [];
  let level: readonly ModuleSection[] = module.sections;
  let index = 0;

  while (index < rest.length) {
    const match = level.find((s) => s.key === rest[index]);
    if (!match) break;
    trail.push(match);
    index += 1;
    if (!match.sections?.length) break; // a screen: anything after it is a record
    level = match.sections;
  }

  const last = trail[trail.length - 1];
  // Only a childless section is a screen. Stopping on a bucket means the URL named the
  // bucket itself, which is a landing page, not a place a record can hang off.
  const isScreen = Boolean(last && !last.sections?.length);
  const recordId = isScreen ? rest[index] : undefined;

  return {
    isRoot: false,
    module,
    trail,
    ...(isScreen && last ? { section: last } : {}),
    ...(recordId ? { recordId } : {}),
  };
}

/**
 * Where entering an application lands.
 *
 * The spec says the first section, falling back to the module home for a module
 * that has no sections defined yet.
 */
export function entryPathFor(module: ModuleDefinition): string {
  return module.sections[0]?.path ?? module.path;
}

/**
 * Builds the full trail: Home › Application › Section › Record.
 *
 * Every level except the last is a link. `recordLabel` is supplied by the page,
 * because a record's display name cannot be derived from its id.
 */
export function buildBreadcrumbs(location: NavigationLocation, recordLabel?: string): Breadcrumb[] {
  const crumbs: Breadcrumb[] = [{ label: 'Home', path: HOME_PATH }];
  if (!location.module) {
    // At the root, "Home" IS the current page, so it stops being a link.
    return [{ label: 'Home' }];
  }

  // `?start=1` — the same escape hatch the drawer's module header carries. The crumb is
  // an explicit request for the product's home, and a product whose welcome screen is
  // skipped by preference would otherwise send the click to the skip target instead —
  // which is how "Inventory Registry" landed on Project Registry (2026-09-21).
  crumbs.push({ label: location.module.navLabel, path: `${location.module.path}?start=1` });

  // Every level the drawer shows gets a crumb. Each bucket has a real landing page, so
  // each is a working link — the trail never skips a level the user can see.
  for (const section of location.trail) {
    crumbs.push({ label: section.label, path: section.path });
  }

  if (location.recordId) {
    crumbs.push({ label: recordLabel ?? location.recordId });
  }

  // The deepest crumb is the current page — drop its link.
  const last = crumbs[crumbs.length - 1]!;
  crumbs[crumbs.length - 1] = { label: last.label };

  return crumbs;
}
